import { Typography } from '@euler/app/components/typography';
import { CardLoading } from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BottomPanel } from '@euler/components/BottomPanel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiDashboardRevisionView } from '@euler/model/bi/dashboard';
import { useServiceFactory } from '@euler/services/factory';
import { memo, useEffect, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiButton } from '../../components/ui/BiButton';
import { BiRetry } from '../../components/ui/BiRetry';
import {
  biTimeLabel,
  kBiRevisionSourceLabels,
} from '../../functions/biDashboardText';

type RevisionsState =
  | { kind: 'loading' }
  | { kind: 'ready'; items: BiDashboardRevisionView[] }
  | { kind: 'failed' };

/**
 * The dashboard's history, newest first. Only the latest change can be
 * undone (spec §12.4): undoing it writes a new revision, so nothing later
 * is ever lost, and undoing the undo redoes it.
 */
export const RevisionsPanel = memo(
  ({
    visible,
    dashboardId,
    revision,
    onUndo,
    onClose,
  }: {
    visible: boolean;
    dashboardId: string;
    /** The dashboard's current revision; reloads the list when it moves. */
    revision: number;
    onUndo: () => void;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { biService } = useServiceFactory();
    const [state, setState] = useState<RevisionsState>({ kind: 'loading' });
    const [reload, setReload] = useState(0);
    useEffect(() => {
      if (!visible) return;
      let active = true;
      setState({ kind: 'loading' });
      biService
        .dashboardRevisions(dashboardId)
        .then(items => {
          if (active) setState({ kind: 'ready', items });
        })
        .catch(error => {
          console.error('BI revisions failed', dashboardId, error);
          if (active) setState({ kind: 'failed' });
        });
      return () => {
        active = false;
      };
    }, [biService, dashboardId, visible, revision, reload]);

    return (
      <BottomPanel visible={visible} onClose={onClose}>
        <View style={{ flex: 1, padding: rpx(24), gap: rpx(12) }}>
          <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
            修改记录
          </Typography.Text>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            可以撤销最近一次修改；撤销本身也会记一条，再撤销一次即可恢复。
          </Typography.Text>
          {state.kind === 'loading' ? (
            <CardLoading label="正在读取记录…" />
          ) : state.kind === 'failed' ? (
            <BiRetry onRetry={() => setReload(value => value + 1)} />
          ) : (
            <ScrollView style={{ flex: 1 }}>
              {state.items.map(item => {
                const latest = item.revision === revision;
                return (
                  <View
                    key={item.revision}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: rpx(12),
                      paddingVertical: rpx(10),
                      borderBottomWidth: 1,
                      borderBottomColor: aiColors.border,
                    }}
                  >
                    <View style={{ flex: 1, gap: rpx(2) }}>
                      <Typography.Text
                        size={rpx(13)}
                        color={aiColors.foreground}
                      >
                        {`#${item.revision} ${
                          kBiRevisionSourceLabels[item.source] ?? ''
                        }`}
                      </Typography.Text>
                      <Typography.Text
                        size={rpx(11)}
                        color={aiColors.mutedForeground}
                      >
                        {[
                          item.createdByName,
                          biTimeLabel(item.createdAt),
                          `${item.cardCount} 张图表`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography.Text>
                    </View>
                    {latest && item.revision > 0 ? (
                      <BiButton title="撤销" onPress={onUndo} />
                    ) : latest ? (
                      <Typography.Text
                        size={rpx(11)}
                        color={aiColors.mutedForeground}
                      >
                        当前
                      </Typography.Text>
                    ) : null}
                  </View>
                );
              })}
            </ScrollView>
          )}
        </View>
      </BottomPanel>
    );
  },
);
